<template>
  <v-row>
    <v-col cols="12">
      <v-card class="tb-card" flat>
        <!-- HEADER BAND -->
        <div class="tb-header pl-base-bg">
          <div class="tb-header-icon">
            <v-icon color="#fff" size="22">mdi-scale-balance</v-icon>
          </div>
          <div>
            <div class="tb-title">Trial Balance</div>
            <div class="tb-subtitle">Every account's standing balance, as of a date</div>
          </div>
        </div>

        <div class="pa-6">
          <v-alert type="info" outlined dense border="left" class="tb-disclaimer">
            This Trial Balance is prepared on an accrual basis from your
            double-entry ledger (Journal Entries). Verify with your
            accountant before relying on it for statutory filings.
          </v-alert>

          <!-- FILTERS -->
          <div class="tb-filter-bar">
            <v-row dense align="center">
              <v-col cols="12" md="3">
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  offset-y
                  max-width="290"
                  min-width="290"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="formattedAsOf"
                      label="As of"
                      readonly
                      outlined
                      dense
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>

                  <v-date-picker
                    v-model="asOf"
                    @input="dateMenu = false"
                  />
                </v-menu>
              </v-col>

              <v-col cols="12" md="9" class="tb-actions">
                <v-btn class="tb-apply-btn" @click="loadTrialBalance" :loading="loading">
                  Apply
                </v-btn>

                <v-btn
                  outlined
                  color="#1d6f42"
                  class="ml-2"
                  :disabled="!trialBalance"
                  @click="downloadExcel"
                >
                  <v-icon left size="16" color="#1d6f42">mdi-file-excel-box</v-icon>
                  Excel
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <template v-if="trialBalance">
            <v-row dense class="mb-6">
              <v-col cols="6" md="4">
                <v-card class="tb-kpi-card tb-kpi-card--value pa-4" flat>
                  <div class="tb-kpi-label">Total Debit</div>
                  <div class="tb-kpi-value">{{ formatINR(trialBalance.totalDebit) }}</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="4">
                <v-card class="tb-kpi-card tb-kpi-card--tax pa-4" flat>
                  <div class="tb-kpi-label">Total Credit</div>
                  <div class="tb-kpi-value">{{ formatINR(trialBalance.totalCredit) }}</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card
                  class="tb-kpi-card pa-4"
                  :class="trialBalance.isBalanced ? 'tb-kpi-card--total' : 'tb-kpi-card--warn'"
                  flat
                >
                  <div class="tb-kpi-label">Status</div>
                  <div class="tb-kpi-value">
                    <v-icon color="#fff" size="18" class="mr-1">
                      {{ trialBalance.isBalanced ? "mdi-check-circle" : "mdi-alert-circle" }}
                    </v-icon>
                    {{ trialBalance.isBalanced ? "Balanced" : "Not Balanced" }}
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <app-ag-grid
              title="Account Balances"
              :columnDefs="columns"
              :rowData="trialBalance.accounts"
              :newBtn="false"
              :refreshBtn="false"
              :mobileCard="accountMobileCard"
              height="height: 480px"
            />
          </template>

          <div v-else class="tb-empty-state">
            Select a date and click Apply to generate the Trial Balance.
          </div>
        </div>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import AppAgGrid from "@/components/common/ag-grid.vue";
import { downloadMultiSheetWorkbook } from "@/utils/xlsx-export";
import { subTypeLabel } from "@/utils/account-labels";

export default {
  name: "TrialBalancePage",
  components: { AppAgGrid },

  data() {
    return {
      loading: false,
      dateMenu: false,
      asOf: new Date().toISOString().slice(0, 10),

      columns: [
        { headerName: "Code", field: "code", minWidth: 90 },
        { headerName: "Account", field: "name", minWidth: 180 },
        { headerName: "Type", field: "type", minWidth: 110 },
        { headerName: "Sub-Type", field: "subType", valueFormatter: (p) => subTypeLabel(p.value), minWidth: 160 },
        { headerName: "Debit", field: "debit", valueFormatter: (p) => this.formatINR(p.value), minWidth: 130 },
        { headerName: "Credit", field: "credit", valueFormatter: (p) => this.formatINR(p.value), minWidth: 130 },
      ],
    };
  },

  computed: {
    trialBalance() {
      return this.$store.state.ledgerReports.trialBalance;
    },

    formattedAsOf() {
      if (!this.asOf) return "";
      const d = new Date(this.asOf);
      return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
    },

    accountMobileCard() {
      return {
        title: (row) => row.name || "-",
        subtitle: (row) => row.code || "-",
        fields: (row) => [
          { label: "Debit", value: this.formatINR(row.debit) },
          { label: "Credit", value: this.formatINR(row.credit) },
        ],
      };
    },
  },

  created() {
    this.loadTrialBalance();
  },

  methods: {
    formatINR(value) {
      if (value === null || value === undefined) return "0";
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(value);
    },

    async loadTrialBalance() {
      this.loading = true;
      try {
        await this.$store.dispatch("ledgerReports/getTrialBalance", { asOf: this.asOf });
      } catch (err) {
        console.error(err);
        this.$toast?.error(
          err?.response?.data?.message || "Failed to load Trial Balance",
        );
      } finally {
        this.loading = false;
      }
    },

    downloadExcel() {
      const t = this.trialBalance;

      const rows = t.accounts.map((r) => [
        r.code,
        r.name,
        r.type,
        subTypeLabel(r.subType),
        Number(r.debit) || 0,
        Number(r.credit) || 0,
      ]);

      rows.push(["", "", "", "Total", Number(t.totalDebit) || 0, Number(t.totalCredit) || 0]);

      downloadMultiSheetWorkbook(
        [
          {
            name: "Trial Balance",
            title: "Trial Balance",
            subtitle: `As of ${this.formattedAsOf}`,
            headers: ["Code", "Account", "Type", "Sub-Type", "Debit", "Credit"],
            rows,
            columnWidths: [10, 24, 12, 20, 14, 14],
            numberColumns: [4, 5],
          },
        ],
        `trial-balance-${this.asOf}.xlsx`,
      );
    },
  },
};
</script>

<style scoped>
.tb-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.tb-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
}

.tb-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.tb-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.tb-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.tb-disclaimer {
  margin-bottom: 20px;
  font-size: 13px;
}

.tb-filter-bar {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 16px 16px 4px;
  margin-bottom: 24px;
}

.tb-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 0;
  padding-top: 4px;
}

.tb-apply-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.tb-kpi-card {
  border-radius: 0;
  color: #fff;
  box-shadow: 0 6px 16px rgba(15, 30, 50, 0.12);
}

.tb-kpi-card--value {
  background: linear-gradient(135deg, #052f5e 0%, #0a5a90 100%);
}
.tb-kpi-card--tax {
  background: linear-gradient(135deg, #7a1f1f 0%, #b23a3a 100%);
}
.tb-kpi-card--total {
  background: linear-gradient(135deg, #0d8478 0%, #15a355 100%);
}
.tb-kpi-card--warn {
  background: linear-gradient(135deg, #92400e 0%, #d97706 100%);
}

.tb-kpi-label {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
}

.tb-kpi-value {
  font-size: 20px;
  font-weight: 700;
  margin-top: 6px;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
}

.tb-empty-state {
  text-align: center;
  padding: 48px 0;
  color: #94a3b8;
  font-size: 14px;
}

.v-menu__content {
  z-index: 9999 !important;
}
</style>
